<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Link } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'

/**
 * Inertia v3 error page (RED-212).
 *
 * Mounted via `Inertia::handleExceptionsUsing()` in AppServiceProvider for
 * 403 / 404 / 419 / 500 / 503. Renders inside the regular AppLayout so the
 * navbar stays consistent — never the vanilla `<iframe>` modal that Inertia
 * falls back to when it sees a non-Inertia HTML response.
 */

const { status, message } = defineProps({
  status: { type: Number, required: true },
  // RED-355: the server's own sentence (an outside data service limiting us, with when to try
  // again) replaces the generic description.
  message: { type: String, default: null },
})

const { t, te } = useI18n()

const title = computed(() => {
  const key = `messages.errors.${status}.title`
  return te(key) ? t(key) : t('messages.errors.404.title')
})

const description = computed(() => {
  if (message) {
    return message
  }
  const key = `messages.errors.${status}.description`
  return te(key) ? t(key) : t('messages.errors.404.description')
})
</script>

<template>
  <AppLayout>
    <div class="container mx-auto py-20 text-center">
      <p class="text-sm uppercase tracking-widest text-slate-400 mb-3">
        {{ status }}
      </p>
      <h1 class="text-3xl sm:text-4xl font-light text-slate-800 mb-4">
        {{ title }}
      </h1>
      <p class="text-slate-500 max-w-md mx-auto mb-8">
        {{ description }}
      </p>
      <Link
        href="/"
        class="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
      >
        <span class="pi pi-home"></span>
        {{ t('messages.errors.backHome') }}
      </Link>
    </div>
  </AppLayout>
</template>
